import { Uploader } from "../custom/uploader";
import { FormControl, FormField, FormItem } from "../ui/form";
import type {
	PathValue,
	FieldValues,
	Path,
	UseFormReturn,
} from "react-hook-form";
import UploadIcon from "../../assets/svgs/common/upload.svg";
import type { Accept } from "react-dropzone";
import Spinner from "../spinner.components";

type FormTextAreaProps<T extends FieldValues> = {
	form: UseFormReturn<T>;
	name: Path<T>;
	label: string;
	required?: boolean;
	maxSize?: number;
	maxFiles: number;
	trackingName?: string;
	multiple?: boolean;
	placeholder?: string;
	description?: string;
	uploadType?: string;
	fetching?: boolean;
};

export const FormCsv = <T extends FieldValues>({
	form,
	name,
	maxSize,
	maxFiles,
	multiple,
	placeholder,
	description,
	uploadType,
	fetching,
}: FormTextAreaProps<T>) => {
	const handleUploadChange = (field: Path<T>, files: File[]) => {
		const mappedFiles = files.map((file) => ({ file })); // Map files to expected structure
		form.setValue(field, mappedFiles as PathValue<T, Path<T>>, {
			shouldDirty: true,
		});
	};

	function AcceptType(): Accept {
		if (uploadType === "csv") {
			return { "text/csv": [] };
		}

		return {};
	}

	return (
		<FormField
			control={form.control}
			name={name}
			render={({ field }) => (
				<FormItem className="h-full">
					<FormControl className="flex flex-col justify-center items-center border border-border border-dashed rounded-xl">
						<div className="relative flex flex-col justify-center px-6 w-full h-80">
							<div className="top-0 absolute bg-gradient-to-b from-background to-transparent rounded-t-xl w-full h-10" />

							<div
								className={
									"flex flex-col bg-transparent justify-center items-center w-full h-full"
								}
							>
								<Uploader
									className="relative flex flex-col justify-end items-center gap-4 py-6 w-full h-full cursor-pointer"
									value={field.value}
									onValueChange={(files) => {
										handleUploadChange(name, files);
									}}
									maxFiles={maxFiles}
									maxSize={maxSize}
									multiple={multiple}
									accept={AcceptType()}
								>
									<span className="flex flex-col items-center gap-1 h-3/5 font-semibold">
										{fetching ? <Spinner width={20} /> : <UploadIcon />}

										{fetching ? `Importing "${field.name}"` : placeholder}
										<span className="text-secondary text-sm">
											{fetching ? "" : description}
										</span>
									</span>
								</Uploader>
							</div>
						</div>
					</FormControl>
				</FormItem>
			)}
		/>
	);
};
